import axios from 'axios'
import { getAccess } from 'src/db/serviceApiSetting'
import { env } from 'src/config/env'
import Message from 'src/app/models/Message'

// Platform type matching server-side enum
export type Platform = 'whatsapp' | 'shopee' | 'lazada' | 'tiktok'

// Generic request interface (platform-agnostic)
export interface UnifiedMessageRequest {
  messaging_product: Platform
  recipient_type?: 'individual' // Required for WhatsApp
  to: string // Recipient ID (buyer_id, customer_id, etc.)
  type: string // Message type (validated by platform helpers)
  text?: {
    body?: string // WhatsApp uses 'body'
    text?: string // Marketplace uses 'text'
    preview_url?: boolean
  }
  image?: {
    link: string
    caption?: string
  }
  sticker?: {
    link: string
  }
  video?: {
    link: string
    caption?: string
  }
  // Other message types can be added here
}

// Generic response interface
export interface UnifiedMessageResponse {
  messages: [{
    id: string
    to: string
    status: 'sent' | 'failed'
  }]
}

/**
 * MessageApiClient
 *
 * Generic API client for sending messages to OUR SERVER's unified endpoint.
 * The server then routes to the appropriate marketplace (Shopee, Lazada, TikTok, etc.)
 *
 * Architecture:
 * - This client calls: /v2/:platform/:sender/messages
 * - Server validates and forwards to marketplace
 * - Platform-specific logic in helpers/
 */
class MessageApiClient {
  private static instance: MessageApiClient

  static getInstance(): MessageApiClient {
    if (!this.instance) {
      this.instance = new MessageApiClient()
    }
    return this.instance
  }

  /**
   * Send message via unified API
   *
   * @param platform - Target platform (shopee, lazada, tiktok, etc.)
   * @param sender - Platform-specific sender ID (account.marketplaceShopId)
   * @param recipientId - Recipient ID (buyer_id, customer_id, etc.)
   * @param message - Message object with content and type
   * @param requestBody - Pre-built request body (from platform helper)
   * @returns Promise with response containing message ID and status
   */
  async sendMessage(
    platform: Platform,
    sender: string,
    recipientId: string,
    message: Message,
    requestBody: UnifiedMessageRequest
  ): Promise<UnifiedMessageResponse> {
    // Get cached access token (24-hour cache)
    const apiAccess = await getAccess()
    if (!apiAccess?.accessToken) {
      console.error('[MessageApiClient] No access token found')
      throw new Error('Failed to retrieve access token')
    }

    // Build API URL - calls OUR server, not marketplace directly
    const url = `${env.SERVER_URL}/v2/${platform}/${sender}/messages`

    console.log(`[MessageApiClient] Sending to our server: ${url}`)
    console.log(`[MessageApiClient] Platform: ${platform}, Sender: ${sender}, Recipient: ${recipientId}`)
    console.log(`[MessageApiClient] Token (first 20 chars): ${apiAccess.accessToken.substring(0, 20)}...`)
    console.log(`[MessageApiClient] Request body:`, JSON.stringify(requestBody, null, 2))

    // Send request with Bearer token to OUR server
    try {
      const response = await axios.post<UnifiedMessageResponse>(
        url,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiAccess.accessToken}`,
          },
        }
      )

      console.log(`[MessageApiClient] Server response:`, response.data)
      return response.data
    } catch (error: any) {
      console.error(`[MessageApiClient] ${platform} send failed:`, error)

      if (error.response) {
        // Server responded with error
        const errorData = error.response.data
        throw new Error(
          errorData?.error?.message ||
          `Failed to send ${platform} message: ${error.response.status}`
        )
      } else {
        // Network or other error
        throw new Error('Service unavailable')
      }
    }
  }
}

export default MessageApiClient.getInstance()
